import { Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { OidcStorageService } from '../storage/oidc-storage.service';
import { OpenIdConfiguration } from '../oidc-security.service';
import { JwtJsSha256CryptoService } from '../utils/jwt-js-sha256-crypto.service';

// garder que code et state
const CALLBACK_PARAMS_TO_CHECK = ['code', 'state', 'token', 'id_token'];

class UriEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

@Injectable({ providedIn: 'root' })
export class UrlService {
  constructor(
    private readonly oidcStorageService: OidcStorageService,
    private readonly jwtCryptoService: JwtJsSha256CryptoService
  ) {}

  getAuthorizeUrl(config: OpenIdConfiguration): Observable<string> {
    return from(this.getExistingOrCreateAuthStateControl(config)).pipe(
      switchMap((state) => {
        return this.createCodeChallenge(config).pipe(
          map((codeChallenge: string) => {
            return this.createAuthorizeUrl(codeChallenge, config.redirectUrl, state, config);
          })
        );
      })
    );
  }

  private createAuthorizeUrl(
    codeChallenge: string,
    redirectUrl: string,
    state: string,
    config: OpenIdConfiguration
  ): string {
    const authorizationEndpoint = config.authWellknownEndpoints.authorizationEndpoint;
    const { clientId, idpIdentifier } = config;

    let params = new HttpParams();
    params = params.set('client_id', clientId);
    params = params.append('redirect_uri', redirectUrl);
    params = params.append('idp_identifier', idpIdentifier);
    params = params.append('response_type', 'code');
    params = params.append('scope', 'openid');
    params = params.append('state', state);
    params = params.append('code_challenge', codeChallenge);
    params = params.append('code_challenge_method', 'S256');

    return `${authorizationEndpoint}?${params}`;
  }

  private createCodeChallenge(config: OpenIdConfiguration): Observable<string> {
    return from(this.createCodeVerifier(config)).pipe(
      switchMap((codeVerifier: string) => {
        return this.jwtCryptoService.generateCodeChallenge(codeVerifier);
      })
    );
  }

  private createHttpParams(existingParams?: string): HttpParams {
    existingParams = existingParams ?? '';

    return new HttpParams({
      fromString: existingParams,
      encoder: new UriEncoder(),
    });
  }

  createBodyForCodeFlowRefreshTokensRequest(refreshToken: string, config: OpenIdConfiguration): string {
    const clientId = config.clientId;
    const clientSecret = config.clientSecret;

    if (!clientId || !clientSecret) {
      return null;
    }

    let params = this.createHttpParams();

    params = params.set('grant_type', 'refresh_token');
    params = params.set('client_id', clientId);
    params = params.set('client_secret', clientSecret);
    params = params.set('refresh_token', refreshToken);

    return params.toString();
  }

  isACallbackUrl(currentUrl: string): boolean {
    return CALLBACK_PARAMS_TO_CHECK.some((x) => !!this.getUrlParameter(currentUrl, x));
  }

  getUrlParameter(urlToCheck: string, name: string): string {
    if (!urlToCheck) {
      return '';
    }

    if (!name) {
      return '';
    }

    name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&#]' + name + '=([^&#]*)');
    const results = regex.exec(urlToCheck);

    return results === null ? '' : decodeURIComponent(results[1]);
  }

  async createBodyForCodeFlowCodeRequest(code: string, config: OpenIdConfiguration): Promise<string> {
    const clientId = config.clientId;
    const clientSecret = config.clientSecret;

    let params = this.createHttpParams();

    params = params.set('grant_type', 'authorization_code');
    params = params.set('client_id', clientId);
    params = params.set('client_secret', clientSecret);

    const codeVerifier = await this.oidcStorageService.getCodeVerifier(config.type);
    if (!codeVerifier) {
      return null;
    }
    params = params.set('code_verifier', codeVerifier);
    params = params.set('code', code);

    params = params.set('redirect_uri', config.redirectUrl);
    return params.toString();
  }

  private async getExistingOrCreateAuthStateControl(config: OpenIdConfiguration): Promise<string> {
    let state = await this.oidcStorageService.getAuthStateControl(config.type);

    if (!state) {
      state = this.createRandom(40);
      await this.oidcStorageService.setAuthStateControl(state, config.type);
    }

    return state;
  }

  private async createCodeVerifier(config: OpenIdConfiguration): Promise<string> {
    const codeVerifier = this.createRandom(67);

    await this.oidcStorageService.setCodeVerifier(codeVerifier, config.type);

    return codeVerifier;
  }

  private createRandom(requiredLength: number): string {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._';

    return Array.from({ length: requiredLength }, () =>
      possible.charAt(Math.floor(Math.random() * possible.length))
    ).join('');
  }
}
